import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Ticket, Hotel, Map, Calendar, Users, Copy, CheckCheck, ExternalLink, Sparkles, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';

const services = [
  {
    id: 1,
    title: 'Compra de Boletos',
    description: 'Accede a boletos exclusivos para Disney y Universal Studios con las mejores tarifas y sin colas.',
    icon: Ticket,
    color: 'from-blue-500 to-indigo-600',
    bg: 'bg-blue-50',
  },
  {
    id: 2,
    title: 'Reservas de Hoteles',
    description: 'Hoteles temáticos cerca de los parques, con descuentos especiales para familias.',
    icon: Hotel,
    color: 'from-pink-500 to-rose-600',
    bg: 'bg-pink-50',
  },
  {
    id: 3,
    title: 'Planificación de Itinerarios',
    description: 'Creamos planes personalizados día a día para maximizar tu diversión y evitar esperas.',
    icon: Calendar,
    color: 'from-purple-500 to-violet-600',
    bg: 'bg-purple-50',
  },
  {
    id: 4,
    title: 'Transporte y Traslados',
    description: 'Traslados cómodos desde el aeropuerto hasta los parques, con opciones familiares y seguras.',
    icon: Map,
    color: 'from-green-500 to-emerald-600',
    bg: 'bg-green-50',
  },
  {
    id: 5,
    title: 'Paquetes Personalizados',
    description: 'Armamos viajes a medida para grupos, familias o parejas, incluyendo comidas y experiencias VIP.',
    icon: Users,
    color: 'from-orange-500 to-amber-600',
    bg: 'bg-orange-50',
  },
];

const HOLA_FLY_URL = 'https://holafly.sjv.io/yqVV0N'; // Reemplaza con tu enlace de afiliado
const PROMO_CODE = 'HAPPYALESSA';

const HolaFlyBanner = () => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(PROMO_CODE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <motion.div
      className="relative overflow-hidden rounded-3xl mb-14"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7 }}
      viewport={{ once: true }}
    >
      {/* Fondo degradado */}
      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 opacity-90" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent" />

      {/* Círculos decorativos */}
      <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full bg-white/10 blur-2xl" />
      <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-white/10 blur-xl" />

      <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 px-8 py-7">
        {/* Info */}
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
            <Tag className="w-7 h-7 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-yellow-300" />
              <span className="text-yellow-300 text-sm font-bold uppercase tracking-widest">
                Oferta Exclusiva
              </span>
            </div>
            <h3 className="text-white text-xl font-extrabold leading-tight">
              5% de descuento en tu SIM con{' '}
              <span className="underline decoration-yellow-300 decoration-2">Hola Fly</span>
            </h3>
            <p className="text-blue-100 text-sm mt-1 max-w-sm">
              Conéctate en el extranjero sin sorpresas. Usa nuestro enlace o copia el código al contratar tu eSIM o SIM física.
            </p>
          </div>
        </div>

        {/* Acciones */}
        <div className="flex flex-col sm:flex-row items-center gap-3 flex-shrink-0">
          <button
            onClick={handleCopy}
            className="flex items-center gap-2 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white px-5 py-3 rounded-xl font-mono font-bold text-sm transition-all duration-200 group"
            title="Copiar código"
          >
            <span className="tracking-widest">{PROMO_CODE}</span>
            <AnimatePresence mode="wait">
              {copied ? (
                <motion.span key="check" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
                  <CheckCheck className="w-4 h-4 text-green-300" />
                </motion.span>
              ) : (
                <motion.span key="copy" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
                  <Copy className="w-4 h-4 opacity-70 group-hover:opacity-100" />
                </motion.span>
              )}
            </AnimatePresence>
          </button>

          <a
            href={HOLA_FLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white text-blue-600 hover:bg-blue-50 px-6 py-3 rounded-xl font-bold text-sm shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            Ir a Hola Fly
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* Toast de copiado */}
      <AnimatePresence>
        {copied && (
          <motion.div
            className="absolute top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white text-xs font-semibold px-4 py-1.5 rounded-full shadow-lg pointer-events-none"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
          >
            ¡Código copiado!
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const Services = () => {
  return (
    <section id="services" className="py-20 bg-gradient-to-br from-indigo-50 via-white to-pink-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-indigo-500 to-pink-600 bg-clip-text text-transparent">
            Nuestros Servicios
          </h2>
          <p className="mt-3 text-gray-500 text-lg max-w-xl mx-auto">
            Todo lo que necesitas para vivir la magia, sin complicaciones.
          </p>
        </motion.div>

        {/* Banner Hola Fly */}
        <HolaFlyBanner />

        {/* Grid de servicios */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.id}
                className="relative bg-white p-6 rounded-3xl shadow-md border border-gray-100 hover:shadow-2xl transition-all duration-300 group overflow-hidden"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -6 }}
              >
                {/* Color de fondo sutil al hover */}
                <div className={`absolute inset-0 ${service.bg} opacity-0 group-hover:opacity-40 transition-opacity duration-300 rounded-3xl`} />

                <div className="relative z-10">
                  <div className={`w-14 h-14 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{service.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-5">{service.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          className="text-center mt-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <Link
            to="#quote"
            className="inline-block bg-gradient-to-r from-indigo-500 to-pink-600 text-white px-10 py-4 rounded-full text-lg font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
          >
            Cotiza tu viaje ahora ✨
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;